import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Wrench, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { isSuperAdmin } from "@/lib/roles";
import { toast } from "sonner";

/**
 * /admin/data-repair — página exclusiva do Super Admin.
 * Detecta bairros/regiões/entregadores com nomes suspeitos (padrões de acento corrompido)
 * e permite editar cada linha inline + aplicar todas em lote.
 */
export default function DataRepair() {
  const { user } = useAuth();
  const canAccess = isSuperAdmin(user);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [findings, setFindings] = useState([]);
  const [edits, setEdits] = useState({}); // { "coll::id": "new_nome" }

  const runScan = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/data-repair/scan");
      const items = r.data.items || [];
      setFindings(items);
      // Pré-preenche com a correção automática (CP850 → UTF-8) quando disponível
      setEdits(Object.fromEntries(items.filter((f) => f.sugestao).map((f) => [`${f.collection}::${f.id}`, f.sugestao])));
      toast.info(`${r.data.count} nome(s) suspeito(s) detectado(s)${r.data.auto_fix ? ` · ${r.data.auto_fix} com correção automática` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao escanear");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (canAccess) runScan(); }, [canAccess]);

  if (!canAccess) {
    return (
      <div className="p-6" data-testid="page-data-repair-denied">
        <div className="border-2 border-rose-300 bg-rose-50 rounded p-4 text-rose-800">
          Esta página é exclusiva do Super Admin.
        </div>
      </div>
    );
  }

  const key = (f) => `${f.collection}::${f.id}`;
  const currentValue = (f) => (edits[key(f)] ?? f.nome);
  const setValue = (f, v) => setEdits((prev) => ({ ...prev, [key(f)]: v }));
  const pending = findings.filter((f) => (edits[key(f)] || "").trim() && edits[key(f)].trim() !== f.nome);

  const applyAll = async () => {
    if (!pending.length) return toast.info("Nenhuma alteração para aplicar");
    setApplying(true);
    try {
      const items = pending.map((f) => ({ collection: f.collection, id: f.id, new_nome: edits[key(f)].trim() }));
      const r = await api.post("/admin/data-repair/apply", { items });
      toast.success(`${r.data.updated} registro(s) renomeado(s)`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} erro(s) — veja o console`, { description: JSON.stringify(r.data.errors) });
      await runScan();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao aplicar");
    } finally { setApplying(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="page-data-repair">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-orange-600" />
            Reparar Dados Corrompidos
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Detecta bairros, regiões e entregadores com nomes que sugerem acentos perdidos (ex: "Jardim It lia" ao invés de "Jardim Itália").
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={runScan} disabled={loading} data-testid="btn-rescan">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Escaneando..." : "Escanear novamente"}
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={applyAll} disabled={applying || !pending.length} data-testid="btn-apply-all">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {applying ? "Aplicando..." : `Aplicar ${pending.length} correção(ões)`}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {findings.length} nome(s) suspeito(s) encontrado(s)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {findings.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              {loading ? "Escaneando..." : "🎉 Nenhum dado corrompido detectado."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-orange-50/60">
                  <TableHead>Coleção</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Nome atual</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Nome corrigido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {findings.map((f, i) => {
                  const changed = (edits[key(f)] || "").trim() && edits[key(f)].trim() !== f.nome;
                  return (
                    <TableRow key={key(f)} data-testid={`repair-row-${i}`} className={changed ? "bg-emerald-50/40" : ""}>
                      <TableCell className="text-xs mono text-muted-foreground">{f.collection}</TableCell>
                      <TableCell className="text-sm">{f.empresa_nome || "—"}</TableCell>
                      <TableCell className="font-medium">{f.nome}</TableCell>
                      <TableCell className="text-xs text-amber-800">{f.reason}{f.sugestao && <span className="block text-emerald-700" data-testid={`repair-sugestao-${i}`}>sugestão: {f.sugestao}</span>}</TableCell>
                      <TableCell>
                        <Input
                          value={currentValue(f)}
                          onChange={(e) => setValue(f, e.target.value)}
                          data-testid={`repair-input-${i}`}
                          placeholder={`Ex: ${f.nome.replace(/\s+/g, " ")}`}
                          className={`h-8 text-sm ${changed ? "border-emerald-500" : ""}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
